import { useState } from "react";
import { Form, Row, Col } from "react-bootstrap";
import CountryPicker from "@components/Forms/CountryPicker";
import type { BusinessUnitFormValues } from "../types";

export interface BusinessUnitFormProps {
  id: string;
  initialValues: BusinessUnitFormValues;
  isSubmitting: boolean;
  onSubmit: (values: BusinessUnitFormValues) => void;
}

export const BusinessUnitForm = ({
  id,
  initialValues,
  isSubmitting,
  onSubmit,
}: BusinessUnitFormProps): JSX.Element => {
  const [values, setValues] = useState<BusinessUnitFormValues>(initialValues);

  const set = <K extends keyof BusinessUnitFormValues>(
    key: K,
    v: BusinessUnitFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: v }));

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    onSubmit(values);
  };

  return (
    <Form id={id} data-testid={id} onSubmit={handleSubmit}>
      <Form.Group className="mb-3">
        <Form.Label htmlFor="business-unit-name">Name</Form.Label>
        <Form.Control
          id="business-unit-name"
          type="text"
          required
          value={values.name}
          onChange={(e) => set("name", e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label htmlFor="business-unit-external-name">
          External Name
        </Form.Label>
        <Form.Control
          id="business-unit-external-name"
          type="text"
          value={values.externalName}
          onChange={(e) => set("externalName", e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label htmlFor="business-unit-street-address">
          Street Address
        </Form.Label>
        <Form.Control
          id="business-unit-street-address"
          type="text"
          value={values.streetAddress}
          onChange={(e) => set("streetAddress", e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label htmlFor="business-unit-street2">Address Line 2</Form.Label>
        <Form.Control
          id="business-unit-street2"
          type="text"
          value={values.street2}
          onChange={(e) => set("street2", e.target.value)}
        />
      </Form.Group>

      <Row>
        <Col sm={3}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="business-unit-city">City</Form.Label>
            <Form.Control
              id="business-unit-city"
              type="text"
              value={values.city}
              onChange={(e) => set("city", e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="business-unit-state">St/Prov/Terr</Form.Label>
            <Form.Control
              id="business-unit-state"
              type="text"
              value={values.state}
              onChange={(e) => set("state", e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="business-unit-postal">Zip/Post</Form.Label>
            <Form.Control
              id="business-unit-postal"
              type="text"
              value={values.postalCode}
              onChange={(e) => set("postalCode", e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group className="mb-3">
            <Form.Label>Country</Form.Label>
            <CountryPicker
              country={values.country || null}
              onCountryChange={(c: BusinessUnitFormValues["country"]) =>
                set("country", c)
              }
            />
          </Form.Group>
        </Col>
      </Row>
    </Form>
  );
};

export default BusinessUnitForm;
